import $ from 'jquery';
import { triggerStateChangedRequest, generateObjRefId } from '~core';
import { addSvgToCanvas } from '~core/image';
import { fabric } from 'fabric';
import { mm2px } from '~src/utils/units';
import { getState } from '../../state';
import Swal from 'sweetalert2';
import { EdgeResizer } from '~src/utils';

// Maximum safe dimensions for filters
const MAX_SAFE_DIMENSION = 4096;

export const addCanvasImage = async (imageUrl, options = {}) => {
  return new Promise((resolve, reject) => {
    if (!imageUrl) {
      const error = new Error('Cannot add image to canvas. Missing image URL');
      reject(error);
      return;
    }

    const isSvg = imageUrl.endsWith('.svg');
    if (isSvg) {
      const imagePromise = addSvgToCanvas(imageUrl);
      return resolve(imagePromise);
    }

    const halfCanvasSizeWidth = Math.floor(window.canvas.width / 2);
    const halfCanvasSizeheight = Math.floor(window.canvas.height / 2);

    const onImageAdd = async (image) => {
      // Define the function to continue adding image (both for normal and resized images)
      const continueAddingImage = (imageToAdd) => {
        imageToAdd.set({
          erasable: false,
          angle: 0,
          giotto: {
            objRefId: generateObjRefId('image'),
          },
          ...options,
        });

        if (options.widthCm || options.heightCm) {
          const format = getState('selected.formato');
          const formatDefault = getState('selected.formatoDefault');
          const dpi = formatDefault.dpi || (format && format.dpi);

          const widthPx = mm2px(options.widthCm, dpi);
          const heightPx = mm2px(options.heightCm, dpi);

          if (options.widthCm && options.heightCm) {
            imageToAdd.set({
              scaleX: widthPx / imageToAdd.width,
              scaleY: heightPx / imageToAdd.height,
            });
          } else if (options.widthCm) {
            imageToAdd.scaleToWidth(widthPx, true);
          } else {
            imageToAdd.scaleToHeight(heightPx, true);
          }

          imageToAdd.set({
            lockScalingX: true,
            lockScalingY: true,
            lockUniScaling: true,
          });
        } else {
          if (imageToAdd.width > imageToAdd.height) {
            if (imageToAdd.width > halfCanvasSizeWidth) {
              imageToAdd.scaleToWidth(halfCanvasSizeWidth);
            }
          } else {
            if (imageToAdd.height > halfCanvasSizeheight) {
              imageToAdd.scaleToHeight(halfCanvasSizeheight);
            }
          }
        }

        // Initialize with empty filters array
        imageToAdd.filters = [];

        window.canvas.add(imageToAdd);
        if (!options.left && !options.top) {
          window.canvas.centerObject(imageToAdd);
        }
        window.canvas.setActiveObject(imageToAdd);
        window.canvas.renderAll();
        $('.editor-sidebar-image-injection-loader').remove();
        triggerStateChangedRequest();

        resolve(imageToAdd);
      };
      // Check if image is too large
      if (
        image.width > MAX_SAFE_DIMENSION ||
        image.height > MAX_SAFE_DIMENSION
      ) {
        const result = await Swal.fire({
          title: 'Immagine troppo grande',
          html: `
            <p>L'immagine è ${image.width}x${image.height} pixel.</p>
            <p>Immagini così grandi potrebbero causare rallentamenti e problemi di prestazioni.</p>
            <p>Vuoi ridurre automaticamente le dimensioni dell'immagine?</p>
          `,
          icon: 'warning',
          showConfirmButton: true,
          showCancelButton: true,
          confirmButtonText: 'Sì, ridimensiona',
          cancelButtonText: 'No, continua così',
          confirmButtonColor: '#278e8e',
          cancelButtonColor: '#d33',
        });

        if (result.isConfirmed) {
          // Store original dimensions for reference
          image.set({
            originalWidth: image.width,
            originalHeight: image.height,
          });

          // Use EdgeResizer to create a resized URL
          const resizer = new EdgeResizer(imageUrl);
          let resizedUrl;

          // Maintain aspect ratio - resize to fit within MAX_SAFE_DIMENSION
          if (image.width > image.height) {
            resizedUrl = resizer.scaleToWidth(MAX_SAFE_DIMENSION);
          } else {
            resizedUrl = resizer.scaleToHeight(MAX_SAFE_DIMENSION);
          }

          // Show loading
          Swal.fire({
            title: 'Ridimensionamento in corso...',
            allowOutsideClick: false,
            allowEscapeKey: false,
            allowEnterKey: false,
            showConfirmButton: false,
            didOpen: () => {
              Swal.showLoading();
            },
          });

          // Load the resized image
          fabric.Image.fromURL(
            resizedUrl,
            (resizedImage) => {
              // Copy all properties from original image to resized one
              resizedImage.set({
                ...image.toObject(),
                width: resizedImage.width,
                height: resizedImage.height,
                originalWidth: image.width,
                originalHeight: image.height,
              });

              // The original image was never added to canvas at this point
              // so we don't need to remove it. Just continue with the resized image.
              image = resizedImage;

              Swal.close();

              // Continue with the rest of the function
              continueAddingImage(image);
            },
            { crossOrigin: 'anonymous' }
          );

          // Return early since we need to wait for the resized image to load
          return;
        }
      }

      // For normal flow (no resize needed), continue immediately
      continueAddingImage(image);
    };

    fabric.Image.fromURL(imageUrl, onImageAdd, {
      crossOrigin: 'anonymous',
    });
  });
};
